// ============================================
// Analyst Engine — PJE v3.1
//
// Three main functions:
//   1. runPhase1()  — BiomarkerCards from raw labs
//   2. runPhase2()  — Formulary matching (extracted from twinEngine.ts)
//   3. assessSeverity() — Severity classification
//
// Also exports helper functions used by twinEngine:
//   - BIOMARKER_ALIASES
//   - biomarkerMatches()
// ============================================

import type {
  LabValue,
  Demographics,
  BiomarkerCard,
  AnalystPhase1Output,
  ModifierSet,
  ProtocolItem,
  AnalystPhase2Output,
  SeverityLevel,
} from '../../types/patientJourney';
import type { Product, Bundle, SafetyRule } from '../../types/formulary';
import type { PJEConfig } from './pjeConfig';
import { getPJEConfig } from './pjeConfig';
import { SEED_SUPPLEMENTS } from '../../data/formulary/seedSupplements';
import { SEED_RX } from '../../data/formulary/seedRx';
import { SEED_LONGEVITY } from '../../data/formulary/seedLongevity';
import { SEED_BUNDLES } from '../../data/formulary/seedBundles';
import { SEED_SAFETY_RULES } from '../../data/formulary/seedSafetyRules';
import { CRITICAL_SAFETY_RULES } from '../../data/criticalSafetyRules';
import { type ResolutionPath, createStep } from '../../types/pipelineProvenance';

// =====================================================================
// Biomarker Alias Map — maps lab biomarker display names (from LabValue)
// to product trigger/target biomarker identifiers (from Product catalog)
// =====================================================================

export const BIOMARKER_ALIASES: Record<string, string[]> = {
  // Vitamin D
  'vitamin_d_25oh': ['vitamin d', 'vitamin d (25-oh)', '25-oh vitamin d', 'vitamin d3', '25-hydroxyvitamin d'],
  'vitamin_d': ['vitamin d', 'vitamin d (25-oh)', '25-oh vitamin d', 'vitamin d3'],
  // B vitamins
  'vitamin_b12': ['b12', 'vitamin b12', 'cobalamin'],
  'folate': ['folate', 'folic acid', 'serum folate'],
  'homocysteine': ['homocysteine', 'hcy'],
  // Minerals
  'rbc_magnesium': ['magnesium rbc', 'rbc magnesium', 'magnesium (rbc)', 'rbc mg'],
  'serum_magnesium': ['magnesium', 'serum magnesium', 'mg'],
  'zinc': ['zinc', 'serum zinc'],
  'selenium': ['selenium', 'serum selenium'],
  'iron': ['iron', 'serum iron', 'iron (serum)'],
  'ferritin': ['ferritin', 'serum ferritin'],
  'iron_saturation': ['iron saturation', 'transferrin saturation', 'tibc', 'iron sat'],
  'calcium': ['calcium', 'serum calcium', 'total calcium'],
  // Inflammation — canonical: hscrp
  'hscrp': ['hscrp', 'hs-crp', 'hs crp', 'c-reactive protein', 'crp', 'hs_crp', 'hsCRP'],
  'esr': ['esr', 'sed rate', 'sedimentation rate'],
  // Lipids
  'triglycerides': ['triglycerides', 'trigs', 'tg'],
  'ldl': ['ldl', 'ldl-c', 'ldl cholesterol', 'ldl_cholesterol', 'lipoprot. (ldl) direct', 'lipoprotein (ldl) direct'],
  'ldl_particle_number': ['ldl-p', 'ldl particle', 'ldl particle number'],
  'omega3_index': ['omega-3 index', 'omega3 index', 'omega 3 index'],
  'total_cholesterol': ['total cholesterol', 'cholesterol', 'tc'],
  'hdl': ['hdl', 'hdl-c', 'hdl cholesterol'],
  'apob': ['apob', 'apolipoprotein b', 'apo b', 'apoB'],
  'lpa': ['lp(a)', 'lipoprotein(a)', 'lipoprotein (a)', 'lipoprotein-a', 'lipoprotein a', 'lp-a'],
  'non_hdl_cholesterol': ['non-hdl cholesterol', 'non hdl cholesterol'],
  'vldl': ['vldl', 'vldl cholesterol'],
  // Metabolic
  'fasting_insulin': ['fasting insulin', 'insulin', 'fasting insulin level'],
  'fasting_glucose': ['fasting glucose', 'fasting blood glucose', 'fbg'],
  'glucose': ['glucose', 'glucose, serum', 'blood glucose', 'random glucose', 'glucose (random)'],
  'hba1c': ['hemoglobin a1c', 'hba1c', 'a1c', 'glycated hemoglobin', 'hgba1c'],
  'homa_ir': ['homa-ir', 'homa ir', 'insulin resistance', 'HOMA-IR'],
  'uric_acid': ['uric acid'],
  'c_peptide': ['c-peptide', 'c peptide'],
  // Thyroid
  'tsh': ['tsh', 'thyroid stimulating hormone'],
  'free_t3': ['free t3', 'ft3', 'free triiodothyronine'],
  'free_t4': ['free t4', 'ft4', 'free thyroxine'],
  'tpo_antibodies': ['tpo', 'tpo antibodies', 'anti-tpo', 'thyroid peroxidase antibodies'],
  'thyroglobulin_antibodies': ['thyroglobulin antibodies', 'anti-thyroglobulin', 'tg antibodies'],
  // Hormones — base names (gender suffix stripped at match time via canonicalId)
  'estradiol': ['estradiol', 'e2', 'estradiol (day 3)'],
  'progesterone': ['progesterone', 'p4', 'progesterone (day 21)'],
  'cortisol_am': ['cortisol', 'cortisol am', 'cortisol (am)', 'morning cortisol'],
  'dheas': ['dhea-s', 'dhea sulfate', 'dheas', 'dhea-sulfate', 'dhea_s', 'dehydroepiandrosterone sulfate'],
  'dhea': ['dhea', 'dehydroepiandrosterone', 'dhea, serum', 'dhea unconjugated'],
  'dheas_male': ['dhea-s', 'dhea sulfate', 'dheas', 'dehydroepiandrosterone sulfate'],
  'dheas_female': ['dhea-s', 'dhea sulfate', 'dheas', 'dehydroepiandrosterone sulfate'],
  'total_testosterone': ['testosterone total', 'total testosterone', 'testosterone', 'total t'],
  'testosterone_total_male': ['testosterone', 'testosterone, total', 'total testosterone', 'testosterone total', 'testosterone, serum'],
  'testosterone_total_female': ['testosterone', 'total testosterone', 'testosterone total'],
  'free_testosterone': ['free testosterone', 'testosterone free', 'free t'],
  'free_testosterone_male': ['free testosterone', 'testosterone free'],
  'free_testosterone_female': ['free testosterone', 'testosterone free'],
  'shbg': ['shbg', 'sex hormone binding globulin'],
  'fsh': ['fsh', 'follicle stimulating hormone'],
  'lh': ['lh', 'luteinizing hormone'],
  'pth': ['pth', 'parathyroid hormone'],
  'psa': ['psa', 'prostate specific antigen'],
  'prolactin': ['prolactin'],
  'leptin': ['leptin'],
  'il6': ['il-6', 'interleukin-6', 'IL_6', 'il6'],
  'tnf_alpha': ['tnf-alpha', 'tumor necrosis factor', 'TNF_alpha', 'tnf alpha'],
  // Blood — CBC
  'hemoglobin': ['hemoglobin', 'hgb', 'hb'],
  'hematocrit': ['hematocrit', 'hct'],
  'wbc': ['wbc', 'white blood cells', 'white blood cell count'],
  'rbc': ['rbc', 'red blood cells', 'red blood cell count'],
  'platelets': ['platelets', 'plt', 'platelet count'],
  'mcv': ['mcv', 'mean corpuscular volume'],
  'mch': ['mch', 'mean corpuscular hemoglobin'],
  'mchc': ['mchc', 'mean corpuscular hgb conc'],
  'rdw': ['rdw', 'red cell distribution width', 'rdw-cv'],
  'mpv': ['mpv', 'mean platelet volume'],
  'neutrophils': ['neutrophils', 'absolute neutrophils', 'neutrophil count'],
  'lymphocytes': ['lymphocytes', 'absolute lymphocytes', 'lymphocyte count'],
  'monocytes': ['monocytes', 'absolute monocytes', 'monocyte count'],
  'eosinophils': ['eosinophils', 'absolute eosinophils'],
  'basophils': ['basophils', 'absolute basophils'],
  // Hepatic
  'alt': ['alt', 'sgpt', 'alanine aminotransferase'],
  'ast': ['ast', 'sgot', 'aspartate aminotransferase'],
  'ggt': ['ggt', 'gamma-glutamyl transferase'],
  'alp': ['alp', 'alkaline phosphatase'],
  'bilirubin_total': ['bilirubin total', 'total bilirubin', 'bilirubin, total'],
  'bilirubin_direct': ['bilirubin direct', 'direct bilirubin', 'bilirubin, direct'],
  'ldh': ['ldh', 'lactate dehydrogenase'],
  // Renal
  'bun': ['bun', 'blood urea nitrogen'],
  'creatinine': ['creatinine', 'cr'],
  'egfr': ['egfr', 'gfr', 'estimated gfr', 'e-gfr'],
  'albumin': ['albumin', 'serum albumin'],
  'sodium': ['sodium', 'na'],
  'potassium': ['potassium', 'k'],
  'chloride': ['chloride', 'cl'],
  'co2': ['co2', 'bicarbonate', 'carbon dioxide'],
  'phosphorus': ['phosphorus', 'phosphate'],
  'total_protein': ['total protein', 'protein, total'],
  'globulin': ['globulin'],
  'ag_ratio': ['a/g ratio', 'albumin/globulin ratio'],
  'bun_creatinine_ratio': ['bun/creatinine ratio'],
  // Iron studies
  'tibc': ['tibc', 'total iron binding capacity'],
  'transferrin_saturation': ['transferrin saturation', 'iron saturation', 'tsat'],
  'uibc': ['uibc', 'unsaturated iron binding capacity'],
  // Cardiovascular
  'bnp': ['bnp', 'brain natriuretic peptide'],
  'nt_probnp': ['nt-probnp', 'nt probnp'],
  'cystatin_c': ['cystatin c', 'cystatin-c'],
  // Autoimmune
  'ana_screen': ['ana', 'ana screen', 'antinuclear antibody'],
  'rf': ['rf', 'rheumatoid factor'],
  // Cancer screening
  'ca_125': ['ca-125', 'ca 125'],
  'psa_free': ['free psa', 'psa, free'],
  // Specialty
  'vitamin_b6': ['vitamin b6', 'pyridoxine', 'b6'],
  'vitamin_a': ['vitamin a', 'retinol'],
  'vitamin_e': ['vitamin e', 'alpha-tocopherol'],
  'mma': ['mma', 'methylmalonic acid'],
  'fibrinogen': ['fibrinogen'],
  'amylase': ['amylase'],
  'lipase': ['lipase'],
  // Heavy metals
  'lead': ['lead', 'blood lead'],
  'mercury': ['mercury', 'blood mercury'],
  'copper': ['copper', 'serum copper'],
  // General
  'general_nutrition': ['general nutrition', 'multiple deficiencies'],
  'gut_health': ['gut health', 'gi symptoms'],
};

// =====================================================================
// Helper Functions (exported for twinEngine backward compat)
// =====================================================================

/**
 * Normalize a biomarker name to lowercase and check if it matches
 * any of the known aliases for a product's trigger/target biomarker.
 */
export function biomarkerMatches(productBiomarker: string, labBiomarker: string): boolean {
  const prodLower = productBiomarker.toLowerCase().trim();
  const labLower = labBiomarker.toLowerCase().trim();

  // Direct match
  if (prodLower === labLower) return true;

  // Check aliases: does the product biomarker have aliases that match the lab name?
  const aliases = BIOMARKER_ALIASES[prodLower];
  if (aliases && aliases.some((a) => a === labLower)) return true;

  // Reverse lookup: does the lab biomarker, when looked up as a product biomarker, have aliases?
  const labAliases = BIOMARKER_ALIASES[labLower];
  if (labAliases && labAliases.some((a) => a === prodLower)) return true;

  // Cross-check: do both resolve to overlapping alias sets?
  if (aliases && labAliases) {
    if (aliases.some((a) => labAliases.includes(a))) return true;
  }

  // Substring fallback: underscore-to-space normalization
  const prodNorm = prodLower.replace(/_/g, ' ').replace(/-/g, ' ');
  const labNorm = labLower.replace(/_/g, ' ').replace(/-/g, ' ');
  if (prodNorm === labNorm) return true;

  return false;
}

/** Match result with provenance — records HOW the match happened. */
export interface BiomarkerMatchResult {
  matched: boolean;
  /** Resolution path taken (null if no match) */
  path: ResolutionPath | null;
  /** Which method: lookup (table) or inference (model) */
  method: 'lookup' | 'inference' | null;
  /** Source table/mechanism used */
  source: string | null;
}

/**
 * Like biomarkerMatches() but returns the resolution path.
 * Every call records whether it was a table lookup or required inference.
 * This is the provenance foundation — the onion layer.
 */
export function biomarkerMatchesWithProvenance(
  productBiomarker: string,
  labBiomarker: string,
  canonicalId?: string,
): BiomarkerMatchResult {
  const prodLower = productBiomarker.toLowerCase().trim();

  // Path 1: canonicalId exact match (greenest green)
  if (canonicalId) {
    const canonLower = canonicalId.toLowerCase().trim();
    if (prodLower === canonLower) {
      return { matched: true, path: 'canonical_id_exact', method: 'lookup', source: 'canonicalId' };
    }
    // Path 2: gender-suffix strip
    const baseCanon = canonLower.replace(/_(?:male|female)$/, '');
    if (prodLower === baseCanon) {
      return { matched: true, path: 'gender_suffix_strip', method: 'lookup', source: 'canonicalId (gender-stripped)' };
    }
  }

  const labLower = labBiomarker.toLowerCase().trim();

  // Path 3: direct name match
  if (prodLower === labLower) {
    return { matched: true, path: 'canonical_id_exact', method: 'lookup', source: 'display_name_exact' };
  }

  // Path 4: alias map forward lookup
  const aliases = BIOMARKER_ALIASES[prodLower];
  if (aliases && aliases.some((a) => a === labLower)) {
    return { matched: true, path: 'alias_map', method: 'lookup', source: 'BIOMARKER_ALIASES (forward)' };
  }

  // Path 5: alias map reverse lookup
  const labAliases = BIOMARKER_ALIASES[labLower];
  if (labAliases && labAliases.some((a) => a === prodLower)) {
    return { matched: true, path: 'alias_map', method: 'lookup', source: 'BIOMARKER_ALIASES (reverse)' };
  }

  // Path 6: alias cross-check
  if (aliases && labAliases) {
    if (aliases.some((a) => labAliases.includes(a))) {
      return { matched: true, path: 'alias_map', method: 'lookup', source: 'BIOMARKER_ALIASES (cross)' };
    }
  }

  // Path 7: underscore/hyphen normalization fallback
  const prodNorm = prodLower.replace(/_/g, ' ').replace(/-/g, ' ');
  const labNorm = labLower.replace(/_/g, ' ').replace(/-/g, ' ');
  if (prodNorm === labNorm) {
    return { matched: true, path: 'alias_map', method: 'lookup', source: 'name_normalization' };
  }

  // No match
  return { matched: false, path: null, method: null, source: null };
}

/** Convert a BiomarkerMatchResult to a ProvenanceStep. */
export function matchResultToStep(result: BiomarkerMatchResult, input: string, output: string) {
  if (!result.matched || !result.path || !result.method) {
    // No formulary product targets this biomarker — correct behavior, scored green
    return createStep('trigger', 'derived', 'no_trigger_match', 'formulary_search_complete', input, output);
  }
  return createStep('trigger', result.method, result.path, result.source ?? undefined, input, output);
}


/**
 * Check if a product's triggers fire based on the patient's triggered labs.
 * Returns the number of matching biomarkers (0 = no match).
 */
function countProductBiomarkerMatches(product: Product, triggeredLabs: LabValue[]): number {
  let matchCount = 0;

  for (const lab of triggeredLabs) {
    // Check targetBiomarkers array
    for (const target of product.targetBiomarkers) {
      if (biomarkerMatches(target, lab.biomarker)) {
        matchCount++;
        break; // Only count each lab once per product
      }
    }
  }

  return matchCount;
}

/**
 * Check if a product's specific trigger conditions match the patient's labs.
 * This checks the trigger thresholds and direction (above/below).
 */
function productTriggersMatch(product: Product, triggeredLabs: LabValue[]): boolean {
  if (product.triggers.length === 0) return false;

  for (const trigger of product.triggers) {
    // "any_suboptimal" triggers match if there are any triggered labs at all
    if (trigger.condition === 'any_suboptimal' && triggeredLabs.length > 0) return true;

    for (const lab of triggeredLabs) {
      if (!biomarkerMatches(trigger.biomarker, lab.biomarker)) continue;

      // Check if the lab status direction matches the trigger condition
      const labIsBelow = lab.status === 'below_functional' || lab.status === 'below_conventional';
      const labIsAbove = lab.status === 'above_functional' || lab.status === 'above_conventional' || lab.status === 'elevated';

      const triggerIsBelow = trigger.condition === 'below_functional' || trigger.condition === 'below_standard';
      const triggerIsAbove = trigger.condition === 'above_functional' || trigger.condition === 'above_standard';

      if ((triggerIsBelow && labIsBelow) || (triggerIsAbove && labIsAbove)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Check if any medication name fuzzy-matches against a drug interaction string.
 * Drug interactions in the catalog are descriptive (e.g., "warfarin_increased_bleeding_risk"),
 * so we check if the medication name appears as a substring.
 */
function medicationMatchesDrugInteraction(medName: string, interactionStr: string): boolean {
  const medLower = medName.toLowerCase().trim();
  const interLower = interactionStr.toLowerCase().replace(/_/g, ' ');
  return interLower.includes(medLower) || medLower.includes(interLower.split(' ')[0]);
}

/**
 * Check if a condition string fuzzy-matches against a contraindication entry.
 */
function conditionMatchesContraindication(condition: string, contraindication: string): boolean {
  const condLower = condition.toLowerCase().trim();
  const contraLower = contraindication.toLowerCase().replace(/_/g, ' ');
  return contraLower.includes(condLower) || condLower.includes(contraLower);
}

/**
 * Check if an allergen matches any allergen flag on a product.
 */
function allergenMatchesFlag(allergen: string, flag: string): boolean {
  const aLower = allergen.toLowerCase().trim();
  const fLower = flag.toLowerCase().replace(/_/g, ' ');
  return fLower.includes(aLower) || aLower.includes(fLower);
}

// =====================================================================
// Range Parsing
// =====================================================================

/**
 * Parse a range string like "3.0-15.0" or "3.0 - 15.0" into { low, high }.
 * Falls back to { low: 0, high: 0 } if unparseable.
 */
function parseRange(rangeStr: string): { low: number; high: number } {
  // Try "low-high" or "low - high" pattern
  const match = rangeStr.match(/([\d.]+)\s*[-–]\s*([\d.]+)/);
  if (match) {
    return { low: parseFloat(match[1]), high: parseFloat(match[2]) };
  }

  // Try "<value" pattern (upper bound only)
  const ltMatch = rangeStr.match(/^<\s*([\d.]+)/);
  if (ltMatch) {
    return { low: 0, high: parseFloat(ltMatch[1]) };
  }

  // Try ">value" pattern (lower bound only)
  const gtMatch = rangeStr.match(/^>\s*([\d.]+)/);
  if (gtMatch) {
    return { low: parseFloat(gtMatch[1]), high: Infinity };
  }

  return { low: 0, high: 0 };
}

// =====================================================================
// Timing Mapping
// =====================================================================

/**
 * Map Product.timing (ProtocolTiming uppercase) to ProtocolItem.timing (lowercase).
 */
function mapTiming(productTiming: string): 'am_energy' | 'midday_absorption' | 'pm_recovery' {
  switch (productTiming) {
    case 'AM_ENERGY': return 'am_energy';
    case 'MIDDAY_ABSORPTION': return 'midday_absorption';
    case 'PM_RECOVERY': return 'pm_recovery';
    default: return 'am_energy';
  }
}

// =====================================================================
// assessSeverity — Severity Classification
// =====================================================================

/**
 * Determine severity level from a set of BiomarkerCards.
 *
 * - If any card has status 'critical' (count >= criticalThreshold) -> 'critical'
 * - If triggered count >= urgentThreshold -> 'urgent'
 * - If any card is triggered -> 'attention'
 * - Otherwise -> 'routine'
 */
export function assessSeverity(
  cards: BiomarkerCard[],
  config?: PJEConfig,
): SeverityLevel {
  const cfg = config ?? getPJEConfig();

  const criticalCount = cards.filter((c) => c.status === 'critical').length;
  const triggeredCount = cards.filter((c) => c.trigger).length;

  if (criticalCount >= cfg.severityRules.criticalThreshold) return 'critical';
  if (triggeredCount >= cfg.severityRules.urgentThreshold) return 'urgent';
  if (triggeredCount > 0) return 'attention';
  return 'routine';
}

// =====================================================================
// runPhase1 — Biomarker Card Generation
// =====================================================================

/**
 * Compute BiomarkerCards from raw lab values against 3 ranges
 * (standard / functional / optimal).
 *
 * For each lab:
 *   - standardRange = conventionalRange parsed from string
 *   - functionalRange = parsed from string
 *   - optimalRange = same as functionalRange (MVP fallback)
 *   - status computed from value vs functional range:
 *       value < functionalRange.low * 0.5 -> 'critical'
 *       value < functionalRange.low       -> 'low'
 *       value > functionalRange.high * 1.5 -> 'critical' (high-side)
 *       value > functionalRange.high       -> 'elevated'
 *       value within functional range inner 50% -> 'optimal'
 *       otherwise -> 'suboptimal'
 *   - trigger = true if status is not 'optimal'
 */
export function runPhase1(
  labs: LabValue[],
  config?: PJEConfig,
): AnalystPhase1Output {
  const cfg = config ?? getPJEConfig();

  const biomarkerCards: BiomarkerCard[] = labs.map((lab) => {
    const standardRange = parseRange(lab.conventionalRange);
    const functionalRange = parseRange(lab.functionalRange);

    // MVP: optimal = functional (per config.severityRules.optimalRangeFallback)
    const optimalRange = cfg.severityRules.optimalRangeFallback === 'use_functional'
      ? { ...functionalRange }
      : { ...functionalRange };

    // Compute status against functional range
    const status = computeStatus(lab.value, functionalRange);
    const trigger = status !== 'optimal';

    return {
      biomarkerId: lab.biomarker,
      name: lab.biomarker,
      value: lab.value,
      unit: lab.unit,
      standardRange,
      functionalRange,
      optimalRange,
      status,
      trigger,
    };
  });

  const severity = assessSeverity(biomarkerCards, cfg);
  const triggeredCount = biomarkerCards.filter((c) => c.trigger).length;
  const criticalMarkers = biomarkerCards
    .filter((c) => c.status === 'critical')
    .map((c) => c.biomarkerId);

  return {
    biomarkerCards,
    severity,
    triggeredCount,
    criticalMarkers,
  };
}

/**
 * Compute the BiomarkerCard status from a value against a functional range.
 */
function computeStatus(
  value: number,
  functionalRange: { low: number; high: number },
): BiomarkerCard['status'] {
  const { low, high } = functionalRange;

  // Guard: if range is invalid (0,0), treat as suboptimal
  if (low === 0 && high === 0) return 'suboptimal';

  // Critical low: value < functionalRange.low * 0.5
  if (value < low * 0.5) return 'critical';

  // Low: value < functionalRange.low
  if (value < low) return 'low';

  // Critical high: value > functionalRange.high * 1.5
  if (value > high * 1.5) return 'critical';

  // Elevated: value > functionalRange.high
  if (value > high) return 'elevated';

  // Optimal: value within functional range inner 50%
  // Inner 50% = the middle 50% of the range
  const rangeSpan = high - low;
  const innerLow = low + rangeSpan * 0.25;
  const innerHigh = high - rangeSpan * 0.25;

  if (value >= innerLow && value <= innerHigh) return 'optimal';

  // Otherwise suboptimal (within functional range but outside inner 50%)
  return 'suboptimal';
}

// =====================================================================
// runPhase2 — Formulary Matching Engine
// =====================================================================

interface BlockedProduct {
  product: Product;
  reason: string;
}

interface WarningProduct {
  product: Product;
  message: string;
}

/**
 * Core formulary matching engine — extracted from twinEngine.ts
 * inferFormularyRecommendations().
 *
 * Takes structured v3.1 inputs (AnalystPhase1Output, ModifierSet, Demographics)
 * and returns structured AnalystPhase2Output with ProtocolItem[].
 *
 * Steps:
 *   1. Select product pool by gate level
 *   2. Merge all safety rules
 *   3. Identify blocked products from safety rules
 *   4. Filter and score products
 *   5. Check for warning-level safety rules
 *   6. Match bundles
 *   7. Build AnalystPhase2Output
 */
export function runPhase2(
  phase1: AnalystPhase1Output,
  modifiers: ModifierSet,
  demographics: Demographics,
  config?: PJEConfig,
): AnalystPhase2Output {
  const cfg = config ?? getPJEConfig();

  // Derive gate level from severity
  const gateLevel: 1 | 2 =
    phase1.severity === 'critical' || phase1.severity === 'urgent' ? 2 : 1;

  // Build triggered labs from biomarker cards (for backward compat with matching logic)
  const triggeredCards = phase1.biomarkerCards.filter((c) => c.trigger);
  const triggeredLabs: LabValue[] = triggeredCards.map(cardToLabValue);

  // Extract modifier fields into flat arrays
  const medications = modifiers.medications;
  const conditions = modifiers.conditions;
  const contraindications = modifiers.contraindications;
  const allergens = modifiers.allergies;
  const sex = demographics.sex;

  // R7: Log formulary source for audit trail
  console.info('[AnalystEngine] Formulary source: SEED_COMPILED. Firestore edits require redeployment.', {
    formularySource: 'SEED_COMPILED',
    seedSupplementCount: SEED_SUPPLEMENTS.length,
    seedRxCount: SEED_RX.length,
    seedLongevityCount: SEED_LONGEVITY.length,
    timestamp: new Date().toISOString(),
  });

  // --- Step 1: Select product pool by gate level ---
  const allProducts: Product[] = gateLevel >= 2
    ? [...SEED_SUPPLEMENTS, ...SEED_RX, ...SEED_LONGEVITY]
    : [...SEED_SUPPLEMENTS];

  // Only active products
  let activeProducts = allProducts.filter((p) => p.status === 'active');

  // Budget filtering: if structural, exclude products above the budget tier
  if (cfg.budgetFiltering === 'structural') {
    const maxTier = budgetTierToMaxProductTier(modifiers.budgetTier);
    activeProducts = activeProducts.filter((p) => p.tier <= maxTier);
  }

  // --- Step 2: Merge all safety rules ---
  const allSafetyRules: SafetyRule[] = [
    ...CRITICAL_SAFETY_RULES,
    ...SEED_SAFETY_RULES,
  ].filter((r) => r.isActive);

  // Deduplicate by ID (critical rules first)
  const ruleMap = new Map<string, SafetyRule>();
  for (const rule of allSafetyRules) {
    if (!ruleMap.has(rule.id)) {
      ruleMap.set(rule.id, rule);
    }
  }

  const medNames = medications.map((m) => m.name.toLowerCase().trim());
  const condLower = conditions.map((c) => c.toLowerCase().trim());
  const allConditions = [...condLower, ...contraindications.map((c) => c.toLowerCase().trim())];
  const allergenLower = allergens.map((a) => a.toLowerCase().trim());
  const sexLower = sex.toLowerCase() as 'female' | 'male';

  // --- Step 3: Identify blocked product IDs from safety rules ---
  const blockedByRules = new Map<string, string>(); // productId -> block reason

  for (const rule of Array.from(ruleMap.values())) {
    if (rule.severity !== 'block') continue;

    let triggered = false;

    switch (rule.triggerType) {
      case 'drug_interaction':
        if (rule.matchMedications) {
          triggered = rule.matchMedications.some((rm) =>
            medNames.some((m) => m.includes(rm.toLowerCase()) || rm.toLowerCase().includes(m)),
          );
        }
        break;
      case 'condition':
        if (rule.matchConditions) {
          triggered = rule.matchConditions.some((rc) =>
            allConditions.some((c) => c.includes(rc.toLowerCase()) || rc.toLowerCase().includes(c)),
          );
        }
        break;
      case 'allergy':
        if (rule.matchAllergens) {
          triggered = rule.matchAllergens.some((ra) =>
            allergenLower.some((a) => a.includes(ra.toLowerCase()) || ra.toLowerCase().includes(a)),
          );
        }
        break;
      case 'pregnancy':
        if (rule.matchConditions) {
          triggered = rule.matchConditions.some((rc) =>
            allConditions.some((c) => c.includes(rc.toLowerCase()) || rc.toLowerCase().includes(c)),
          );
        }
        break;
    }

    if (triggered) {
      for (const pid of rule.affectedProductIds) {
        if (!blockedByRules.has(pid)) {
          blockedByRules.set(pid, rule.message);
        }
      }
    }
  }

  // --- Step 4: Filter and score products ---
  const recommended: Array<{ product: Product; matchCount: number; triggerMatch: boolean; goalMatchCount: number }> = [];
  const blocked: BlockedProduct[] = [];
  const warnings: WarningProduct[] = [];

  for (const product of activeProducts) {
    // Gender filter
    if (product.gender !== 'any' && product.gender !== sexLower) continue;

    // Check if product triggers or targets match the patient's labs
    const matchCount = countProductBiomarkerMatches(product, triggeredLabs);
    const triggerMatch = productTriggersMatch(product, triggeredLabs);

    // Skip products with no biomarker relevance
    if (matchCount === 0 && !triggerMatch) continue;

    // Check if blocked by safety rules
    if (blockedByRules.has(product.id)) {
      blocked.push({ product, reason: blockedByRules.get(product.id)! });
      continue;
    }

    // Check product-level drug interactions
    let productBlocked = false;
    for (const interaction of product.drugInteractions) {
      for (const med of medications) {
        if (medicationMatchesDrugInteraction(med.name, interaction)) {
          blocked.push({
            product,
            reason: `Drug interaction with ${med.name}: ${interaction.replace(/_/g, ' ')}`,
          });
          productBlocked = true;
          break;
        }
      }
      if (productBlocked) break;
    }
    if (productBlocked) continue;

    // Check product-level contraindications
    let productContraindicated = false;
    for (const ci of product.contraindications) {
      for (const cond of allConditions) {
        if (conditionMatchesContraindication(cond, ci)) {
          blocked.push({
            product,
            reason: `Contraindication: ${ci.replace(/_/g, ' ')}`,
          });
          productContraindicated = true;
          break;
        }
      }
      if (productContraindicated) break;
    }
    if (productContraindicated) continue;

    // Check allergen flags
    let productAllergen = false;
    for (const flag of product.allergenFlags) {
      for (const allg of allergenLower) {
        if (allergenMatchesFlag(allg, flag)) {
          blocked.push({
            product,
            reason: `Allergen flag: ${flag.replace(/_/g, ' ')} (patient allergen: ${allg})`,
          });
          productAllergen = true;
          break;
        }
      }
      if (productAllergen) break;
    }
    if (productAllergen) continue;

    // Product is safe and relevant — score goal overlap
    const goalMatchCount = modifiers.goals.length > 0
      ? product.targetGoals.filter((g) => modifiers.goals.includes(g)).length
      : 0;
    recommended.push({ product, matchCount, triggerMatch, goalMatchCount });
  }

  // Sort: trigger matches first, then goal match count, then biomarker match count, then sortOrder
  recommended.sort((a, b) => {
    if (a.triggerMatch !== b.triggerMatch) return a.triggerMatch ? -1 : 1;
    if (a.goalMatchCount !== b.goalMatchCount) return b.goalMatchCount - a.goalMatchCount;
    if (a.matchCount !== b.matchCount) return b.matchCount - a.matchCount;
    return a.product.sortOrder - b.product.sortOrder;
  });

  // --- Step 4b: Cluster cap — max 2 products per trigger biomarker ---
  // Runs AFTER sort so highest-priority products survive the cap
  const biomarkerProductCount = new Map<string, number>();
  const clustered: typeof recommended = [];
  for (const r of recommended) {
    const triggerBio = findPrimaryTriggerBiomarker(r.product, triggeredLabs);
    const bioKey = triggerBio.toLowerCase().trim();
    if (bioKey && bioKey !== 'general') {
      const count = biomarkerProductCount.get(bioKey) ?? 0;
      if (count >= 2) continue; // cluster cap: skip 3rd+ product for same biomarker
      biomarkerProductCount.set(bioKey, count + 1);
    }
    clustered.push(r);
  }

  // Replace recommended with cluster-capped list
  recommended.length = 0;
  recommended.push(...clustered);

  // --- Step 5: Check for warning-level safety rules on recommended products ---
  const recommendedProductIds = new Set(recommended.map((r) => r.product.id));
  for (const rule of Array.from(ruleMap.values())) {
    if (rule.severity !== 'warn') continue;

    let triggered = false;

    switch (rule.triggerType) {
      case 'drug_interaction':
        if (rule.matchMedications) {
          triggered = rule.matchMedications.some((rm) =>
            medNames.some((m) => m.includes(rm.toLowerCase()) || rm.toLowerCase().includes(m)),
          );
        }
        break;
      case 'combination':
      case 'duplicate':
        if (rule.matchProductIds) {
          triggered = rule.matchProductIds.some((pid) => recommendedProductIds.has(pid));
        }
        break;
      case 'condition':
        if (rule.matchConditions) {
          triggered = rule.matchConditions.some((rc) =>
            allConditions.some((c) => c.includes(rc.toLowerCase()) || rc.toLowerCase().includes(c)),
          );
        }
        break;
    }

    if (triggered) {
      const affectedRecommended = recommended.filter((r) =>
        rule.affectedProductIds.length === 0 || rule.affectedProductIds.includes(r.product.id),
      );
      if (affectedRecommended.length > 0) {
        for (const rec of affectedRecommended) {
          warnings.push({ product: rec.product, message: rule.message });
        }
      }
    }
  }

  // --- Step 6: Match bundles ---
  const matchedBundles: Array<{ bundle: Bundle; matchCount: number }> = [];
  const labValueMap = new Map(triggeredLabs.map((l) => [l.biomarker.toLowerCase().trim(), l]));

  for (const bundle of SEED_BUNDLES) {
    // Gender filter
    if (bundle.gender !== 'any' && bundle.gender !== sexLower) continue;

    // Tier filter
    if (bundle.tier > gateLevel) continue;

    // Only active bundles
    if (bundle.status !== 'active') continue;

    // Check trigger conditions
    const { trigger } = bundle;
    let conditionMatches = 0;

    for (const cond of trigger.conditions) {
      // Try to find the lab by biomarker name (case-insensitive, alias-aware)
      let matchedLab: LabValue | undefined;

      const condBioLower = cond.biomarker.toLowerCase().trim();

      // Direct match first
      matchedLab = labValueMap.get(condBioLower);

      // Try alias matching if direct failed
      if (!matchedLab) {
        for (const lab of triggeredLabs) {
          if (biomarkerMatches(condBioLower, lab.biomarker)) {
            matchedLab = lab;
            break;
          }
        }
      }

      if (!matchedLab) continue;

      // Evaluate the operator
      const numValue = typeof cond.value === 'number' ? cond.value : parseFloat(cond.value);
      if (isNaN(numValue)) continue;

      let matches = false;
      switch (cond.operator) {
        case 'lt': matches = matchedLab.value < numValue; break;
        case 'gt': matches = matchedLab.value > numValue; break;
        case 'eq': matches = matchedLab.value === numValue; break;
        case 'between':
          matches = cond.value2 !== undefined
            ? matchedLab.value >= numValue && matchedLab.value <= cond.value2
            : false;
          break;
      }

      if (matches) conditionMatches++;
    }

    if (conditionMatches >= trigger.minimumMatchCount) {
      matchedBundles.push({ bundle, matchCount: conditionMatches });
    }
  }

  // Sort bundles by match count descending
  matchedBundles.sort((a, b) => b.matchCount - a.matchCount);

  // --- Step 7: Build structured output ---

  // Convert recommended products to ProtocolItems
  const protocol: ProtocolItem[] = recommended.map((r) => {
    const p = r.product;

    // Find the primary trigger biomarker that matched
    const triggerBiomarker = findPrimaryTriggerBiomarker(p, triggeredLabs);

    // Build reasoning from trigger descriptions
    const reasoning = p.triggers
      .filter((t) => triggeredLabs.some((l) => biomarkerMatches(t.biomarker, l.biomarker)))
      .map((t) => t.description)
      .filter(Boolean)
      .join('; ') || p.rationale;

    return {
      productId: p.id,
      productName: p.name,
      dosage: p.dosage,
      timing: mapTiming(p.timing),
      reasoning,
      triggerBiomarker,
      tier: p.tier as 1 | 2 | 3,
    };
  });

  // Deduplicate blocked products
  const seenBlockedIds = new Set<string>();
  const blockedProducts = blocked
    .filter((b) => {
      if (seenBlockedIds.has(b.product.id)) return false;
      seenBlockedIds.add(b.product.id);
      return true;
    })
    .map((b) => ({
      productName: b.product.name,
      reason: b.reason,
    }));

  // Deduplicate warnings
  const seenWarnings = new Set<string>();
  const uniqueWarnings = warnings
    .filter((w) => {
      const key = `${w.product.id}:${w.message}`;
      if (seenWarnings.has(key)) return false;
      seenWarnings.add(key);
      return true;
    })
    .map((w) => ({
      productName: w.product.name,
      message: w.message,
    }));

  return {
    protocol,
    blockedProducts,
    warnings: uniqueWarnings,
    modifiersApplied: modifiers,
    gateLevel,
  };
}

// =====================================================================
// formatProtocolAsString — Backward-compatible string formatter
// =====================================================================

/**
 * Format AnalystPhase2Output as the same string format the old
 * inferFormularyRecommendations() returned, for backward compatibility.
 */
export function formatProtocolAsString(output: AnalystPhase2Output): string {
  const sections: string[] = [];

  // RECOMMENDED products
  if (output.protocol.length > 0) {
    const recLines = output.protocol.map((item) => {
      const whyClause = item.reasoning ? ` | WHY: ${item.reasoning}` : '';
      return `- ${item.productName} — ${item.dosage} (${item.timing.replace(/_/g, ' ')})${whyClause}`;
    });
    sections.push(`RECOMMENDED PRODUCTS:\n${recLines.join('\n')}`);
  }

  // BLOCKED products
  if (output.blockedProducts.length > 0) {
    const blockLines = output.blockedProducts.map((b) =>
      `- BLOCKED: ${b.productName} — ${b.reason}`,
    );
    sections.push(`BLOCKED PRODUCTS (do NOT recommend these):\n${blockLines.join('\n')}`);
  }

  // WARNINGS
  if (output.warnings.length > 0) {
    const warnLines = output.warnings.map((w) =>
      `- WARNING (${w.productName}): ${w.message}`,
    );
    sections.push(`WARNINGS (mention if relevant):\n${warnLines.join('\n')}`);
  }

  if (sections.length === 0) {
    return 'No specific product triggers identified from this patient\'s labs. Focus on lifestyle recommendations (nutrition, exercise, sleep, stress management).';
  }

  let result = sections.join('\n\n');

  if (output.gateLevel === 1) {
    result += '\n\nREMINDER: Gate 1 — SUPPLEMENT recommendations only. Do NOT mention HRT, bioidentical hormones, or any prescription therapies.';
  } else {
    result += '\n\nNOTE: Gate 2 is active — you may now discuss provider consultation, telehealth, and prescription options where appropriate.';
  }

  return result;
}

// =====================================================================
// Internal Helpers
// =====================================================================

/**
 * Convert a BiomarkerCard back to a LabValue for backward-compatible
 * matching logic that expects LabValue[].
 */
function cardToLabValue(card: BiomarkerCard): LabValue {
  // Map BiomarkerCard.status to LabValue.status
  const statusMap: Record<BiomarkerCard['status'], LabValue['status']> = {
    'optimal': 'optimal',
    'suboptimal': 'below_functional',
    'low': 'below_functional',
    'elevated': 'above_functional',
    'critical': 'below_conventional', // Could be either side; matching logic handles direction
  };

  // For critical status, determine direction by comparing value to range midpoint
  let labStatus = statusMap[card.status];
  if (card.status === 'critical') {
    const midpoint = (card.functionalRange.low + card.functionalRange.high) / 2;
    labStatus = card.value < midpoint ? 'below_conventional' : 'above_conventional';
  }

  return {
    biomarker: card.biomarkerId,
    value: card.value,
    unit: card.unit,
    conventionalRange: `${card.standardRange.low}-${card.standardRange.high}`,
    functionalRange: `${card.functionalRange.low}-${card.functionalRange.high}`,
    status: labStatus,
    trigger: card.trigger,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Find the primary trigger biomarker that caused a product to be recommended.
 */
function findPrimaryTriggerBiomarker(product: Product, triggeredLabs: LabValue[]): string {
  // Check product triggers first
  for (const trigger of product.triggers) {
    for (const lab of triggeredLabs) {
      if (biomarkerMatches(trigger.biomarker, lab.biomarker)) {
        return lab.biomarker;
      }
    }
  }

  // Fall back to targetBiomarkers
  for (const target of product.targetBiomarkers) {
    for (const lab of triggeredLabs) {
      if (biomarkerMatches(target, lab.biomarker)) {
        return lab.biomarker;
      }
    }
  }

  return 'general';
}

/**
 * Convert ModifierSet.budgetTier to a max product tier number for filtering.
 */
function budgetTierToMaxProductTier(budgetTier: ModifierSet['budgetTier']): number {
  switch (budgetTier) {
    case 'tier1_only': return 1;
    case 'tier1_tier2': return 2;
    case 'all_tiers': return 4; // Allow all tiers (1-4)
    default: return 4;
  }
}
